import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { fetchUserPerformance } from './userPerformance.service';
import { buildOverviewViewModel, OverviewViewModel } from './overview.model';
import { PeriodRange } from './PeriodPicker';

/**
 * State machine for the Overview data request. Modeling it as a discriminated union
 * keeps illegal combinations (e.g. loading + error) unrepresentable.
 */
export type UserPerformanceState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'error' }
  | { status: 'success'; data: OverviewViewModel };

interface UseUserPerformanceParams {
  userUuid?: string;
  period: PeriodRange | null;
}

/**
 * Owns the fetch lifecycle for the user-performance report, keeping the Overview
 * component purely presentational. Refetches whenever the user or period changes,
 * cancels in-flight requests on change/unmount, and exposes a manual `reload`.
 */
export const useUserPerformance = ({
  userUuid,
  period,
}: UseUserPerformanceParams): [UserPerformanceState, () => void] => {
  const [state, setState] = useState<UserPerformanceState>({ status: 'idle' });
  const [reloadToken, setReloadToken] = useState(0);

  const reload = useCallback(() => setReloadToken((token) => token + 1), []);

  useEffect(() => {
    if (!userUuid || !period) {
      setState({ status: 'idle' });
      return undefined;
    }

    const abort = new AbortController();
    setState({ status: 'loading' });

    fetchUserPerformance({
      user: userUuid,
      startDate: period[0].format('YYYY-MM-DD'),
      endDate: period[1].format('YYYY-MM-DD'),
      abort,
    })
      .then((response) => setState({ status: 'success', data: buildOverviewViewModel(response) }))
      .catch((err) => {
        if (axios.isCancel(err)) return;
        setState({ status: 'error' });
      });

    return () => abort.abort();
  }, [userUuid, period, reloadToken]);

  return [state, reload];
};

export default useUserPerformance;
